import { useState, useEffect } from 'react';
import TopNavbar from '../../components/ui/TopNavbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import UserListItem from './components/UserListItem';
import UserProfile from './components/UserProfile';
import PermissionMatrix from './components/PermissionMatrix';
import AuditTrail from './components/AuditTrail';
import SessionManagement from './components/SessionManagement';
import AddUserModal from './components/AddUserModal';
import BulkImportModal from './components/BulkImportModal';

const UserAndRoleManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);

  const [users, setUsers] = useState([
    {
      id: 'USR001',
      name: 'Michael Anderson',
      email: 'michael.anderson@hcc.com',
      role: 'Admin',
      department: 'Administration',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 3600000),
      loginCount: 1247,
      createdAt: new Date('2023-01-15'),
    },
    {
      id: 'USR002',
      name: 'Sarah Mitchell',
      email: 'sarah.mitchell@hcc.com',
      role: 'Ops',
      department: 'Operations',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 7200000),
      loginCount: 2156,
      createdAt: new Date('2023-02-20'),
    },
    {
      id: 'USR003',
      name: 'David Chen',
      email: 'david.chen@hcc.com',
      role: 'Finance',
      department: 'Finance',
      status: 'active',
      mfaEnabled: false,
      lastLogin: new Date(Date.now() - 86400000),
      loginCount: 892,
      createdAt: new Date('2023-03-10'),
    },
    {
      id: 'USR004',
      name: 'Emma Thompson',
      email: 'emma.thompson@hcc.com',
      role: 'Ops',
      department: 'Operations',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 14400000),
      loginCount: 1834,
      createdAt: new Date('2023-04-05'),
    },
    {
      id: 'USR005',
      name: 'James Wilson',
      email: 'james.wilson@hcc.com',
      role: 'RNR',
      department: 'Trading',
      status: 'active',
      mfaEnabled: false,
      lastLogin: new Date(Date.now() - 172800000),
      loginCount: 456,
      createdAt: new Date('2023-05-12'),
    },
    {
      id: 'USR006',
      name: 'Olivia Martinez',
      email: 'olivia.martinez@hcc.com',
      role: 'Finance',
      department: 'Finance',
      status: 'inactive',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 604800000),
      loginCount: 1123,
      createdAt: new Date('2023-06-18'),
    },
    {
      id: 'USR007',
      name: 'Robert Taylor',
      email: 'robert.taylor@hcc.com',
      role: 'Ops',
      department: 'Operations',
      status: 'suspended',
      mfaEnabled: false,
      lastLogin: new Date(Date.now() - 1209600000),
      loginCount: 234,
      createdAt: new Date('2023-07-22'),
    },
    {
      id: 'USR008',
      name: 'Sophia Brown',
      email: 'sophia.brown@hcc.com',
      role: 'Admin',
      department: 'Administration',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 10800000),
      loginCount: 987,
      createdAt: new Date('2023-08-30'),
    },
    {
      id: 'USR009',
      name: 'Patricia Reynolds',
      email: 'patricia.reynolds@hcc.com',
      role: 'Business Manager',
      department: 'Management',
      location: 'Brisbane',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 7200000),
      loginCount: 245,
      createdAt: new Date('2025-12-13'),
    },
    {
      id: 'USR010',
      name: 'Marcus Johnson',
      email: 'marcus.johnson@hcc.com',
      role: 'Logistics Coordinator',
      department: 'Operations',
      location: 'Brisbane',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 3600000),
      loginCount: 189,
      createdAt: new Date('2025-12-13'),
    },
    {
      id: 'USR011',
      name: 'Rachel Kim',
      email: 'rachel.kim@hcc.com',
      role: 'Finance Analyst',
      department: 'Finance',
      location: 'Brisbane',
      status: 'active',
      mfaEnabled: true,
      lastLogin: new Date(Date.now() - 5400000),
      loginCount: 312,
      createdAt: new Date('2025-12-13'),
    },
  ]);

  const [permissions, setPermissions] = useState({
    shipment: { view: true, create: true, edit: true, delete: false, export: true },
    cost: { view: true, create: true, edit: true, delete: false, export: true },
    fee: { view: true, create: false, edit: false, delete: false, export: true },
    issue: { view: true, create: true, edit: true, delete: false, export: true },
    review: { view: true, create: true, edit: true, delete: false, export: true },
    user: { view: false, create: false, edit: false, delete: false },
    master: { view: true, create: false, edit: false, delete: false },
    reports: { view: true, export: true },
    accountsPayable: { view: true, create: true, edit: true, approve: true, export: true },
    accountsReceivable: { view: true, create: true, edit: true, approve: true, export: true },
    invoices: { view: true, create: true, edit: true, approve: true, export: true },
    bankReconciliation: { view: true, create: true, edit: true, export: true },
    exchangeRates: { view: true, create: false, edit: true, export: true },
    tradingOperations: { view: true, create: true, edit: true, export: true },
    operationsManagement: { view: true, create: true, edit: true, export: true },
  });

  const [auditLogs, setAuditLogs] = useState([
    {
      id: 'AUD001',
      type: 'login',
      action: 'Successful Login',
      description: 'User logged in successfully from desktop',
      timestamp: new Date(Date.now() - 3600000),
      ipAddress: '203.45.67.89',
      details: 'Browser: Chrome 120.0 | OS: Windows 11',
    },
    {
      id: 'AUD002',
      type: 'permission',
      action: 'Permission Updated',
      description: 'Shipment delete permission granted',
      timestamp: new Date(Date.now() - 86400000),
      performedBy: 'Michael Anderson',
      details: 'Module: Shipment Register | Permission: Delete | Status: Enabled',
    },
    {
      id: 'AUD003',
      type: 'profile',
      action: 'Profile Updated',
      description: 'User email address changed',
      timestamp: new Date(Date.now() - 172800000),
      performedBy: 'Sarah Mitchell',
      details: 'Old: sarah.m@example.com | New: sarah.mitchell@hcc.com',
    },
    {
      id: 'AUD004',
      type: 'data',
      action: 'Data Export',
      description: 'Exported shipment register data',
      timestamp: new Date(Date.now() - 259200000),
      ipAddress: '203.45.67.89',
      details: 'Format: CSV | Records: 150 | Date Range: 01-Nov-2024 to 30-Nov-2024',
    },
    {
      id: 'AUD005',
      type: 'login',
      action: 'Failed Login Attempt',
      description: 'Invalid password entered',
      timestamp: new Date(Date.now() - 345600000),
      ipAddress: '192.168.1.100',
      details: 'Attempt: 1 of 3 | Browser: Firefox 119.0',
    },
  ]);

  const [activeSessions, setActiveSessions] = useState([
    {
      id: 'SES001',
      device: 'Desktop - Windows 11',
      browser: 'Chrome 120.0',
      location: 'Sydney, Australia',
      ipAddress: '203.45.67.89',
      startTime: new Date(Date.now() - 3600000),
      lastActivity: new Date(Date.now() - 300000),
      isCurrent: true,
    },
    {
      id: 'SES002',
      device: 'Mobile - iPhone 15',
      browser: 'Safari 17.0',
      location: 'Melbourne, Australia',
      ipAddress: '203.45.67.90',
      startTime: new Date(Date.now() - 86400000),
      lastActivity: new Date(Date.now() - 43200000),
      isCurrent: false,
    },
  ]);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Ops', label: 'Operations' },
    { value: 'Finance', label: 'Finance' },
    { value: 'RNR', label: 'RNR' },
    { value: 'Business Manager', label: 'Business Manager' },
    { value: 'Logistics Coordinator', label: 'Logistics Coordinator' },
    { value: 'Finance Analyst', label: 'Finance Analyst' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ];

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: 'User' },
    { id: 'permissions', label: 'Permissions', icon: 'Shield' },
    { id: 'audit', label: 'Audit Trail', icon: 'FileText' },
    { id: 'sessions', label: 'Active Sessions', icon: 'Monitor' },
  ];

  useEffect(() => {
    if (users?.length > 0 && !selectedUserId) {
      setSelectedUserId(users?.[0]?.id);
    }
  }, [users, selectedUserId]);

  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const selectedUser = users?.find((u) => u?.id === selectedUserId);

  const handleAddUser = (userData) => {
    // Generate new user ID
    const newUserId = `USR${String(users?.length + 1)?.padStart(3, '0')}`;
    
    const newUser = {
      id: newUserId,
      ...userData,
      status: 'active',
      loginCount: 0,
      lastLogin: new Date(),
      createdAt: new Date(),
    };
    
    setUsers([...users, newUser]);
    alert('✅ User added successfully!');
  };

  const handleUpdateUser = (userId, formData) => {
    setUsers(
      users?.map((u) =>
        u?.id === userId
          ? { ...u, ...formData }
          : u
      )
    );
    alert('✅ User updated successfully!');
  };

  const handleDeleteUser = (userId) => {
    if (confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      setUsers(users?.filter((u) => u?.id !== userId));
      setSelectedUserId(null);
      alert('✅ User deleted successfully!');
    }
  };

  const handleUpdatePermissions = (userId, updatedPermissions) => {
    setPermissions(updatedPermissions);
    alert('✅ Permissions updated successfully!');
  };

  const handleBulkImport = (file) => {
    console.log('Processing bulk import:', file);
    // TODO: Implement bulk import processing
    alert('✅ Bulk import completed!');
  };

  const handleExportUsers = () => {
    alert(`✅ Exporting ${users?.length} users...`);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Section - positioned below TopNavbar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm mt-[65px]">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-semibold text-foreground">
                User & Role Management
              </h1>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  onClick={() => setIsBulkImportModalOpen(true)}
                >
                  Bulk Import
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={handleExportUsers}
                >
                  Export
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="UserPlus"
                  onClick={() => setIsAddUserModalOpen(true)}
                >
                  Add User
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              Manage user accounts, roles, permissions, and security settings
            </p>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-5">
                <div className="bg-card rounded-lg border border-border">
                  <div className="p-4 border-b border-border">
                    <div className="space-y-3">
                      <Input
                        type="search"
                        placeholder="Search users by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Select
                          options={roleOptions}
                          value={filterRole}
                          onChange={setFilterRole}
                          placeholder="Filter by role"
                        />
                        <Select
                          options={statusOptions}
                          value={filterStatus}
                          onChange={setFilterStatus}
                          placeholder="Filter by status"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {filteredUsers?.length} user{filteredUsers?.length !== 1 ? 's' : ''} found
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-success" />
                        <span className="text-muted-foreground text-xs">
                          {users?.filter((u) => u?.status === 'active')?.length} Active
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar">
                    {filteredUsers?.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon
                          name="UserX"
                          size={48}
                          className="mx-auto text-muted-foreground mb-3"
                        />
                        <p className="text-sm text-muted-foreground">
                          No users found matching your filters
                        </p>
                      </div>
                    ) : (
                      filteredUsers?.map((user) => (
                        <UserListItem
                          key={user?.id}
                          user={user}
                          isSelected={selectedUserId === user?.id}
                          onClick={() => setSelectedUserId(user?.id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-7">
                {selectedUser ? (
                  <div className="space-y-6">
                    <div className="bg-card rounded-lg border border-border">
                      <div className="flex items-center border-b border-border overflow-x-auto">
                        {tabs?.map((tab) => (
                          <button
                            key={tab?.id}
                            onClick={() => setActiveTab(tab?.id)}
                            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                              activeTab === tab?.id
                                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <Icon name={tab?.icon} size={18} />
                            <span>{tab?.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {activeTab === 'profile' && (
                      <UserProfile
                        user={selectedUser}
                        onUpdate={handleUpdateUser}
                        onDelete={handleDeleteUser}
                      />
                    )}

                    {activeTab === 'permissions' && (
                      <PermissionMatrix
                        user={selectedUser}
                        permissions={permissions}
                        onUpdate={handleUpdatePermissions}
                      />
                    )}

                    {activeTab === 'audit' && (
                      <AuditTrail userId={selectedUser?.id} auditLogs={auditLogs} />
                    )}

                    {activeTab === 'sessions' && (
                      <SessionManagement
                        userId={selectedUser?.id}
                        activeSessions={activeSessions}
                      />
                    )}
                  </div>
                ) : (
                  <div className="bg-card rounded-lg border border-border p-12 text-center">
                    <Icon
                      name="Users"
                      size={64}
                      className="mx-auto text-muted-foreground mb-4"
                    />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No User Selected
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Select a user from the list to view and manage their details
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onAdd={handleAddUser}
      />
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
};

export default UserAndRoleManagement;